// src/pages/Dashboard/Admin/DepensesRecettesDashboard/components/OperationsMobileCards.jsx

import React from "react";
import { FaExternalLinkAlt, FaImage, FaPen, FaTrash } from "react-icons/fa";
import { PiFilePdfFill } from "react-icons/pi";

import { toAbsoluteDepensesRecettesPhotoUrl } from "../../../../../services/depensesrecettesclubPhotoServices";

import {
  fmtEUR,
  formatDateFR,
  getPlanById,
  getPlanLabel,
} from "../utils/accountingDashboardUtils";

function isPdfUrl(url = "") {
  return /\.pdf(\?|$)/i.test(String(url || ""));
}

export default function OperationsMobileCards({
  rows,
  planComptables,
  onEdit,
  onDelete,
}) {
  return (
    <div className="space-y-3 lg:hidden">
      {rows.map((operation) => {
        const isDepense = operation?.type_operation === "Dépense";
        const plan = getPlanById(planComptables, operation?.plan_comptable_id);
        const hasPdf = isPdfUrl(operation?.photo_url);

        return (
          <article
            key={operation.id}
            className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950"
          >
            <div className="flex gap-3">
              <div className="shrink-0">
                {operation?.photo_url ? (
                  hasPdf ? (
                    <a
                      href={toAbsoluteDepensesRecettesPhotoUrl(
                        operation.photo_url
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50 text-rose-700 ring-1 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                    >
                      <PiFilePdfFill className="text-2xl" />
                    </a>
                  ) : (
                    <img
                      src={toAbsoluteDepensesRecettesPhotoUrl(
                        operation.photo_url
                      )}
                      alt={operation.designation || "Justificatif"}
                      className="h-16 w-16 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                    />
                  )
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800">
                    <FaImage />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-bold text-slate-900 dark:text-white">
                      {operation?.designation || "—"}
                    </h3>

                    <p className="mt-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
                      {formatDateFR(operation?.date_operation)} · Saison{" "}
                      {operation?.saison || "—"}
                    </p>
                  </div>

                  <span
                    className={`shrink-0 rounded-xl px-2.5 py-1 text-sm font-bold ${
                      isDepense
                        ? "bg-rose-50 text-rose-700 ring-1 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                        : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                    }`}
                  >
                    {fmtEUR(operation?.montant)}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-bold text-white ${
                      isDepense ? "bg-rose-600" : "bg-emerald-600"
                    }`}
                  >
                    {operation?.type_operation || "—"}
                  </span>

                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {operation?.categorie || "—"}
                  </span>

                  {plan ? (
                    <span
                      title={getPlanLabel(
                        planComptables,
                        operation?.plan_comptable_id
                      )}
                      className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                    >
                      {plan.code}
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
                      À affecter
                    </span>
                  )}
                </div>

                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                  <span className="font-bold">Bénéficiaire :</span>{" "}
                  {operation?.beneficiaire || "—"}
                </p>

                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  <span className="font-bold">Règlement :</span>{" "}
                  {operation?.type_reglement || "—"}
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {operation?.lien_url && (
                    <a
                      href={operation.lien_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-xl bg-sky-50 px-3 py-2 text-xs font-bold text-sky-700 ring-1 ring-sky-100 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20"
                    >
                      <FaExternalLinkAlt />
                      Lien
                    </a>
                  )}

                  <button
                    type="button"
                    onClick={() => onEdit(operation)}
                    className="inline-flex items-center gap-2 rounded-xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 ring-1 ring-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                  >
                    <FaPen />
                    Modifier
                  </button>

                  <button
                    type="button"
                    onClick={() => onDelete(operation.id)}
                    className="inline-flex items-center gap-2 rounded-xl bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 ring-1 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                  >
                    <FaTrash />
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}